import { useEffect, useState } from "react";
import { api, API, PUBLIC_APP_URL } from "../lib/api";
import { Copy, Check, X, Mail, Linkedin, Sparkles, MessageCircle, Trophy, Share2 } from "lucide-react";

/**
 * One-tap share for a founder's Capital Readiness Score.
 *
 * Behaviours:
 *  - On open, fetches the founder's referral share URL (so every signup
 *    from a shared score becomes a referral credit for them).
 *  - Pre-fills a celebratory share message including their score.
 *  - Channels: email, WhatsApp, LinkedIn, copy link.
 *
 * Props:
 *   open              boolean — show/hide
 *   onClose           () => void
 *   score             number — required (0–100)
 *   companyName       string — optional, defaults to "My company"
 *   celebration       boolean — when true, shows a 🎉 first-score banner header
 *   anchor            "dashboard" | "detailed" | "first-score" — analytics tag (optional)
 */
export default function ShareScoreModal({ open, onClose, score = 0,
                                          companyName = "My company",
                                          celebration = false,
                                          anchor = "dashboard" }) {
  const [shareUrl, setShareUrl] = useState("");
  const [code, setCode] = useState("");
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  // Personalised share-card preview (the founder's actual score baked into
  // an OG-style image). Fetched as a blob so the modal can render an
  // authenticated <img> via object URL — credentialled fetch doesn't work
  // for raw <img src>, hence this dance.
  const [previewUrl, setPreviewUrl] = useState(null);
  // Web Share API availability — used for the "Share with image" primary CTA
  // (declared up here so hooks always run in the same order regardless of
  // `open`, satisfying react-hooks/rules-of-hooks).
  const [nativeAvailable, setNativeAvailable] = useState(false);
  useEffect(() => {
    if (typeof navigator === "undefined") return;
    setNativeAvailable(typeof navigator.share === "function");
  }, []);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    let objectUrl = null;
    (async () => {
      try {
        const r = await fetch(`${API}/founder/share-card.png?score=${score}`,
                               { credentials: "include", cache: "no-store" });
        if (!r.ok || cancelled) return;
        const blob = await r.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setPreviewUrl(objectUrl);
      } catch (_e) { /* preview is best-effort */ }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      setPreviewUrl(null);
    };
  }, [open, score]);

  useEffect(() => {
    if (!open) return;
    setCopied(false);
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get("/founder/referrals/me");
        if (!cancelled) {
          setCode(data.code || "");
          // Prefer the referrals share URL so the recipient lands on /signup?ref=.
          // Falls back to the canonical production /signup so a preview-rendered
          // modal can't leak preview URLs into shared messages.
          setShareUrl(data.share_url || `${PUBLIC_APP_URL}/signup`);
        }
      } catch (_e) {
        if (!cancelled) {
          setShareUrl(`${PUBLIC_APP_URL}/signup`);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  if (!open) return null;

  const scoreText = `${score}/100`;
  const headline = celebration
    ? `🎉 My business just scored ${scoreText} on Clinq's Capital Readiness Score.`
    : `My business scored ${scoreText} on Clinq's Capital Readiness Score.`;
  const shareMessage =
    `${headline} It's a funder-grade assessment of how investable my company is — ` +
    `built for African founders. Want to see where you stand?\n\n${shareUrl}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareMessage);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch (_e) { setCopied(true); setTimeout(() => setCopied(false), 2000); }
  };

  const shareEmail = () => {
    const subject = encodeURIComponent(`I scored ${scoreText} on Clinq's Capital Readiness Score`);
    const body = encodeURIComponent(shareMessage);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const shareWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(shareMessage)}`,
      "_blank", "noopener");
  };

  const shareLinkedIn = () => {
    // LinkedIn shareArticle takes a URL only — best path for now is to open
    // the share dialog and let the user paste their custom message.
    const url = encodeURIComponent(shareUrl);
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
      "_blank", "noopener");
  };

  // Native Web Share API path — used on mobile + iPad + supported desktop.
  // Tries to attach the **personalised** share card (the founder's real score
  // baked into the OG-style image). Falls back to the static `og-share.jpg`
  // if the personalised render endpoint is unavailable, then to text-only.
  const shareNative = async () => {
    const baseData = {
      title: "Clinq — Capital Readiness Score",
      text: shareMessage,
      url: shareUrl,
    };
    // 1) Try personalised card first
    const tryFetch = async (url, opts) => {
      try {
        const r = await fetch(url, opts);
        if (r.ok) return await r.blob();
      } catch (_e) { /* ignore */ }
      return null;
    };
    let blob = await tryFetch(`${API}/founder/share-card.png?score=${score}`,
                              { credentials: "include", cache: "no-store" });
    let fileName = "clinq-my-readiness-score.png";
    let mime = "image/png";
    // 2) Fall back to the static OG thumbnail
    if (!blob) {
      blob = await tryFetch("/og-share.jpg?v=2026022b", { cache: "force-cache" });
      fileName = "clinq-capital-ready.jpg";
      mime = "image/jpeg";
    }
    if (blob) {
      try {
        const file = new File([blob], fileName, { type: mime });
        const dataWithFile = { ...baseData, files: [file] };
        if (navigator.canShare?.(dataWithFile)) {
          await navigator.share(dataWithFile);
          return;
        }
      } catch (_e) { /* fall through */ }
    }
    // 3) Text-only native share
    try {
      if (navigator.canShare?.(baseData)) await navigator.share(baseData);
      else await navigator.share?.(baseData);
    } catch (_e) { /* user dismissed */ }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center bg-black/60 p-0 sm:items-center sm:p-4"
         data-testid="share-score-modal"
         onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
           className="w-full max-w-md overflow-hidden rounded-t-2xl bg-white shadow-2xl sm:rounded-2xl"
           data-anchor={anchor}>
        {/* Header */}
        <div className={"relative px-5 py-4 text-white " +
          (celebration
            ? "bg-gradient-to-br from-amber-500 via-amber-600 to-[#0A8B85]"
            : "bg-gradient-to-br from-[#0A8B85] to-[#066667]")}>
          <button onClick={onClose} data-testid="share-score-close"
                  className="absolute right-3 top-3 rounded p-1.5 text-white/80 hover:bg-white/10 hover:text-white">
            <X className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-2">
            {celebration
              ? <Trophy className="h-5 w-5 text-amber-200" />
              : <Sparkles className="h-5 w-5 text-amber-200" />}
            <div className="text-xs font-bold uppercase tracking-wider text-white/80">
              {celebration ? "Score unlocked" : "Share your score"}
            </div>
          </div>
          <h2 className="mt-1 text-lg font-extrabold leading-tight" data-testid="share-score-headline">
            {celebration
              ? `Nice — you just hit ${scoreText}!`
              : `Share your Capital Readiness Score`}
          </h2>
          <p className="mt-1 text-xs text-white/85">
            Every founder who signs up from your link earns you{" "}
            <span className="font-bold text-amber-200">+1 re-analysis credit</span>.
          </p>
        </div>

        {/* Score card */}
        <div className="px-5 py-4">
          <div className="rounded-2xl border border-neutral-200 bg-gradient-to-br from-neutral-50 to-white p-4 text-center"
               data-testid="share-score-card">
            <div className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">
              {companyName} · CCR Score
            </div>
            <div className="mt-1 text-5xl font-extrabold text-[#0A8B85]">
              {score}
              <span className="text-base font-bold text-neutral-400"> /100</span>
            </div>
          </div>

          {/* Personalised share-card preview — exactly what recipients see */}
          {previewUrl && (
            <div className="mt-3" data-testid="share-score-image-preview">
              <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-neutral-500">
                Image you&apos;re sharing
              </p>
              <img
                src={previewUrl}
                alt="Your personalised Clinq share card"
                className="w-full rounded-xl border border-neutral-200 shadow-sm"
                loading="lazy"
              />
            </div>
          )}

          {/* Preview */}
          <div className="mt-4 rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-2.5 text-xs leading-relaxed text-neutral-700"
               data-testid="share-score-preview">
            {shareMessage}
          </div>

          {/* Native share with image — primary CTA on mobile / iPad */}
          {nativeAvailable && (
            <button
              onClick={shareNative}
              data-testid="share-score-native"
              className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#0A8B85] px-4 py-2.5 text-sm font-bold text-white shadow-md hover:bg-[#066667]"
              title="Open the device share sheet with the Clinq thumbnail attached"
            >
              <Share2 className="h-4 w-4" /> Share with image
            </button>
          )}

          {/* Channels */}
          <div className="mt-4 grid grid-cols-4 gap-2">
            <button onClick={shareWhatsApp} data-testid="share-score-whatsapp"
                    title="Share on WhatsApp"
                    className="flex flex-col items-center gap-1 rounded-xl border border-neutral-200 bg-white px-2 py-2.5 text-[11px] font-semibold text-neutral-700 hover:bg-neutral-50">
              <MessageCircle className="h-4 w-4 text-green-600" />
              WhatsApp
            </button>
            <button onClick={shareLinkedIn} data-testid="share-score-linkedin"
                    title="Share on LinkedIn"
                    className="flex flex-col items-center gap-1 rounded-xl border border-neutral-200 bg-white px-2 py-2.5 text-[11px] font-semibold text-neutral-700 hover:bg-neutral-50">
              <Linkedin className="h-4 w-4 text-sky-700" />
              LinkedIn
            </button>
            <button onClick={shareEmail} data-testid="share-score-email"
                    title="Share by email"
                    className="flex flex-col items-center gap-1 rounded-xl border border-neutral-200 bg-white px-2 py-2.5 text-[11px] font-semibold text-neutral-700 hover:bg-neutral-50">
              <Mail className="h-4 w-4 text-rose-600" />
              Email
            </button>
            <button onClick={copy} data-testid="share-score-copy"
                    title="Copy share text + link"
                    className="flex flex-col items-center gap-1 rounded-xl border border-[#0A8B85]/30 bg-[#0A8B85]/5 px-2 py-2.5 text-[11px] font-semibold text-[#066667] hover:bg-[#0A8B85]/10">
              {copied
                ? <><Check className="h-4 w-4" /> Copied</>
                : <><Copy className="h-4 w-4" /> Copy</>}
            </button>
          </div>

          {loading && (
            <div className="mt-3 text-center text-[11px] text-neutral-400">Loading your share link…</div>
          )}
          {!loading && code && (
            <div className="mt-3 text-center text-[11px] text-neutral-500" data-testid="share-score-code">
              Your referral code: <span className="font-mono font-bold text-neutral-700">{code}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
